import RestCrudThunks from './restCrudThunksGenerator';
import { GLOBAL_DEBOUNCE_TIME, ROLE_TYPES } from '../utils/DataConstants';
import { WALKTHROUGHS_CRUD_ACTION_CREATORS } from '../actions/walkthroughs_actions';
import { getBackendUri } from '../utils/EnvConfigUtils';
import FetchUtils, { parseJSON } from './fetch_utils';
import { logoutSuccess } from '../actions/session_actions';
import { message } from 'antd';
import {
	fetchAllMessagesError,
	fetchAllMessagesStart,
	fetchAllMessagesSuccess,
} from '../actions/messages_actions';
import { normalizeNotesResponse } from './messages_thunks';
import { flattenArrayValues, nullSafeGet, nullSafeGetOrElse } from '../utils/DataAccessUtils';
import WalkthroughPipeLineFilters from '../components/site_surveys_detail_details_page/walkthroughs_pipeline_filters';
import { collapseHighlightAnalyzerVariants } from '../utils/SearchHighlightUtils';
import { formatDateForParam } from '../utils/DataFormatterUtils';
import moment from 'moment';

const debounce = require('debounce-promise');

const fetchUtils = new FetchUtils();

export const walkthroughsRestCrudThunksForBuyer = new RestCrudThunks(
	'walkthroughs',
	'walkthrough',
	'walkthrough',
	'walkthroughs',
	WALKTHROUGHS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	false,
	'site_survey/'
);

export const walkthroughsRestCrudThunksForSupplier = new RestCrudThunks(
	'walkthroughs',
	'walkthrough',
	'walkthrough',
	'walkthroughs',
	WALKTHROUGHS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'site_survey/'
);

export const walkthroughSearchAndAPIAdaptor = (walkthroughs) =>
	walkthroughs.map(({ source: walkthrough, highlight, jsonHighlight }) => {
		if (highlight) {
			highlight = collapseHighlightAnalyzerVariants(highlight);
		}
		// Nested taskAnswers matches arrive via jsonHighlight (from inner_hits), not the top-level
		// highlight bag — the backend emits one entry per matched sub-field, keyed by
		// siteSurveyTaskTitleId, with `field` carrying the specific sub-field that matched
		// (taskAnswers.answer / taskAnswers.taskListAnswers / taskAnswers.checklistAnswers /
		// taskAnswers.comments). We keep that sub-field so the row can label WHERE the match was
		// (e.g. a comment vs the answer). Older docs / a generic match may still arrive as plain
		// 'taskAnswers' (no sub-field), so accept that too. Keep each value paired with its task id so
		// the row can deep-link to the exact task on the detail page. These are nested INSIDE
		// `highlight` (rather than as a sibling field) on purpose: getEntitiesByIdWithSearch only
		// re-applies `highlight` (not other search-only decorations) onto a record that was previously
		// fetched via /read, so a top-level field would be dropped after visiting the detail page and
		// navigating back.
		const taskAnswerHighlights =
			jsonHighlight && jsonHighlight.length > 0
				? jsonHighlight
						.filter(
							(item) =>
								item &&
								item.value &&
								(item.field === 'taskAnswers' || item.field.startsWith('taskAnswers.'))
						)
						.map((item) => ({
							value: item.value,
							siteSurveyTaskTitleId: item.siteSurveyTaskTitleId,
							// The matched task's title and group, echoed from the inner-hit _source (retrieve-only),
							// so the row can label WHICH task/section the highlight came from.
							title: item.title,
							groupTitle: item.groupTitle,
							// '' for the generic 'taskAnswers' (no sub-field); otherwise the part after the dot
							// (answer / comments / checklistAnswers / taskListAnswers).
							subField: item.field === 'taskAnswers' ? '' : item.field.replace('taskAnswers.', ''),
						}))
				: [];
		// Note-text matches also arrive via jsonHighlight (from the nested noteDetails inner_hits), one
		// entry per matched note, each carrying that note's id so the row can deep-link to — and the
		// notes page can scroll to + flash — the exact note. The flat concatenated `notes` highlight
		// field was removed from the search doc, so this is now the ONLY source of note-match highlights.
		// The note object's id is a number but jsonHighlight emits `id` as a string; keep it a string
		// here so the deep-link param and the eventual comparison are both string-based. Folded under
		// `highlight` (not a top-level field) for the same reason as taskAnswerHighlights above — only
		// `highlight` survives the /read round-trip in getEntitiesByIdWithSearch.
		//
		// OpenSearch emits the highlight under several analyzer variants (`noteDetails.text`,
		// `noteDetails.text.prefixed`, `noteDetails.text.stemmed`) — accept any of them (the same suffixes
		// collapseHighlightAnalyzerVariants recognizes), so a note that only matched through a variant still
		// surfaces a pill. A note can match under more than one variant, so dedupe by noteId — keeping the
		// first entry per note in jsonHighlight order — to render a single pill per note. Unlike
		// collapseHighlightAnalyzerVariants this does not rank base > prefixed > stemmed; first-wins matches
		// the taskAnswerHighlights block above.
		const seenNoteIds = new Set();
		const noteHighlights =
			jsonHighlight && jsonHighlight.length > 0
				? jsonHighlight
						.filter(
							(item) =>
								item &&
								item.value &&
								(item.field === 'noteDetails.text' || item.field.startsWith('noteDetails.text.'))
						)
						.map((item) => ({
							value: item.value,
							noteId: item.id != null ? String(item.id) : null,
						}))
						.filter((noteHighlight) => {
							if (noteHighlight.noteId === null) {
								return true;
							}
							if (seenNoteIds.has(noteHighlight.noteId)) {
								return false;
							}
							seenNoteIds.add(noteHighlight.noteId);
							return true;
						})
				: [];
		const mergedHighlight =
			taskAnswerHighlights.length > 0 || noteHighlights.length > 0
				? {
						...(highlight || {}),
						...(taskAnswerHighlights.length > 0 ? { taskAnswerHighlights } : {}),
						...(noteHighlights.length > 0 ? { noteHighlights } : {}),
				  }
				: highlight;
		const result: any = {
			...walkthrough,
			id: parseInt(walkthrough.id),
			highlight: mergedHighlight,
			searchRecord: true,
		};

		// OpenSearch-backed walkthrough rows carry the overall score as flat fields
		// (overallScore/overallPercentageScore/overallMaxPossibleScore/overallMinPossibleScore) instead of
		// the nested `scores` array, which is no longer indexed. Normalize back to the DB-backed `scores`
		// shape here — the single place that knows the search-vs-DB difference — so every consumer
		// (score pill / heatmap via getWalkthroughScoreObject) keeps working off one shape. Only the
		// "Overall Score" group is flattened in the index; per-group scores remain on the DB-backed detail page.
		if (!Array.isArray(walkthrough.scores) && walkthrough.overallScore != null) {
			result.scores = [
				{
					groupTitle: 'Overall Score',
					score: walkthrough.overallScore,
					percentageScore: walkthrough.overallPercentageScore,
					maxPossibleScore: walkthrough.overallMaxPossibleScore,
					minPossibleScore: walkthrough.overallMinPossibleScore,
				},
			];
		}

		if (walkthrough.locationName !== undefined || walkthrough.locationId !== undefined) {
			result.location = {
				id: walkthrough.locationId,
				name: walkthrough.locationName,
				timeZoneId: walkthrough.locationTimeZoneId,
				storeId: walkthrough.locationStoreId,
			};
		}

		// OpenSearch-backed rows carry the site survey as flat fields; DB-backed records nest it under
		// `siteSurvey`. Rebuild the nested shape (same treatment as `location` above) so consumers read
		// one shape everywhere.
		//
		// Note the search doc does NOT carry `walkthroughHeatMapRanges` — only the DB-backed detail
		// page hydrates it (via SiteSurveyServiceImpl, which also resolves template inheritance). Score
		// pills on search-backed list rows therefore fall back to the default colors; add a flat
		// `siteSurveyWalkthroughHeatMapRanges` to WalkthroughOSDoc to light them up.
		const flatSiteSurveyFields = {
			...(walkthrough.siteSurveyId !== undefined ? { id: walkthrough.siteSurveyId } : {}),
			...(walkthrough.siteSurveyTitle !== undefined ? { title: walkthrough.siteSurveyTitle } : {}),
			...(walkthrough.siteSurveyWalkthroughHeatMapRanges !== undefined
				? { walkthroughHeatMapRanges: walkthrough.siteSurveyWalkthroughHeatMapRanges }
				: {}),
		};
		// Merge rather than replace: a record that already came back from /read keeps its richer nested
		// siteSurvey, and only the fields the search doc actually carries are layered on top.
		if (Object.keys(flatSiteSurveyFields).length > 0) {
			result.siteSurvey = {
				...(walkthrough.siteSurvey || {}),
				...flatSiteSurveyFields,
			};
		}

		return result;
	});

const getThunksByRole = (userType) =>
	userType === ROLE_TYPES.SUPPLIER
		? walkthroughsRestCrudThunksForSupplier
		: walkthroughsRestCrudThunksForBuyer;

const fetchWalkthroughsFromOpenSearch = (props) => {
	const {
		dispatch,
		userType,
		additionalParams,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		ignoreRefreshCounts,
	} = props;

	!ignoreRefreshCounts &&
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? refreshWalkthroughCountsForSupplier(filters)
				: refreshWalkthroughCountsForBuyer(filters)
		);

	const body = flattenArrayValues({ ...(additionalParams || {}), ...filters });

	return dispatch(
		getThunksByRole(userType).searchWithPostMethod(
			body,
			targetCollectionName,
			pagination,
			sorting,
			filters,
			false,
			walkthroughSearchAndAPIAdaptor
		)
	);
};

const debouncedOpenSearchFetchByCollection = new Map();
const getDebouncedOpenSearchFetch = (targetCollectionName) => {
	const key = targetCollectionName || '_default';
	if (!debouncedOpenSearchFetchByCollection.has(key)) {
		debouncedOpenSearchFetchByCollection.set(
			key,
			debounce(fetchWalkthroughsFromOpenSearch, GLOBAL_DEBOUNCE_TIME)
		);
	}
	return debouncedOpenSearchFetchByCollection.get(key);
};

export const fetchWalkthroughsWithSearch =
	(userType) =>
	(
		additionalParams,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		ignoreRefreshCounts = false
	) =>
	(dispatch) =>
		getDebouncedOpenSearchFetch(targetCollectionName)({
			dispatch,
			userType,
			additionalParams,
			targetCollectionName,
			pagination,
			sorting,
			filters,
			ignoreRefreshCounts,
		});

export const fetchWalkthroughsWithSearchForBuyer = fetchWalkthroughsWithSearch(ROLE_TYPES.BUYER);
export const fetchWalkthroughsWithSearchForSupplier = fetchWalkthroughsWithSearch(
	ROLE_TYPES.SUPPLIER
);

// The walkthrough list is served by the OpenSearch `/search` endpoint (via the adaptor). These are the
// public entry points consumed by the index/list pages; they alias the search fetch directly.
export const fetchWalkthroughsForBuyer = fetchWalkthroughsWithSearchForBuyer;
export const fetchWalkthroughsForSupplier = fetchWalkthroughsWithSearchForSupplier;

// The walkthrough heatmap needs every completed walkthrough in the rendered window (last 6 months) so it
// can bucket scores by day. The OpenSearch `/search` path does NOT honor `no_pagination` — it falls back
// to a server-side page size of 10 — so we request an explicit large page size and push the completedAt
// window into the query to keep the result set bounded well under OpenSearch's 10k max_result_window.
const HEATMAP_OPENSEARCH_PAGE_SIZE = 10000;

export const fetchWalkthroughsForHeatmap =
	(userType) => (siteSurveyId, startDate, endDate) => (dispatch) => {
		const filters = {
			siteSurveyId,
			completedAtStartDate: formatDateForParam(moment(startDate)),
			completedAtEndDate: formatDateForParam(moment(endDate)),
			no_pagination: true,
		};
		return dispatch(
			getThunksByRole(userType).searchWithPostMethod(
				filters,
				'walkthroughHeatmap',
				{ current: 1, pageSize: HEATMAP_OPENSEARCH_PAGE_SIZE },
				null,
				filters,
				false,
				walkthroughSearchAndAPIAdaptor
			)
		);
	};

export const updateWalkthroughTaskAnswer = (userType) => (taskId, entity) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${userType}/site_survey/walkthroughs/${taskId}/task_answer`;

	return (dispatch) => {
		return new Promise((resolve, reject) => {
			fetchUtils
				.post(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data: entity,
					})
				)
				.then((response) => response.json())
				.then((d) => {
					if (d.status === 'error') {
						dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
						reject(d.message);
					} else {
						dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updatePartialSuccess(d.data));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
					reject(d.message);
				});
		});
	};
};

export const downloadWalkthroughsCSV =
	(roleType) =>
	(params, filters = null) => {
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${roleType}/site_survey/walkthroughs/csv_report_generator`;

		// if sorting present, fix order naming and add to query params
		if (filters) {
			params = {
				...params,
				...filters,
			};
		}

		return (dispatch) => {
			return new Promise((resolve, reject) => {
				fetchUtils
					.get(
						baseUrl + apiUrl,
						Object.assign(
							{ headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
							params
						)
					)
					.then(parseJSON)
					.then(({ json, status, ok }) => {
						const d = json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (d.status === 'error') {
							reject(d.message);
							message.error(d.data);
						} else {
							resolve(d.data);
							const hide = message.loading(d.data, 0);
							setTimeout(hide, 7000);
						}
					})
					.catch((d) => {
						reject(d.message);
					});
			});
		};
	};

export const downloadWalkthroughCSV =
	(roleType) =>
	(id, params = {}, filters = null) => {
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${roleType}/site_survey/walkthroughs/csv_report_generator/${id}`;

		// if sorting present, fix order naming and add to query params
		if (filters) {
			params = {
				...params,
				...filters,
			};
		}

		return (dispatch) => {
			return new Promise((resolve, reject) => {
				fetchUtils
					.get(
						baseUrl + apiUrl,
						Object.assign(
							{ headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
							params
						)
					)
					.then(parseJSON)
					.then(({ json, status, ok }) => {
						const d = json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (d.status === 'error') {
							reject(d.message);
							message.error(d.data);
						} else {
							resolve(d.data);
							const hide = message.loading(d.data, 0);
							setTimeout(hide, 7000);
						}
					})
					.catch((d) => {
						reject(d.message);
					});
			});
		};
	};

export const downloadWalkthroughScoreCSV = (roleType) => (id) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/site_survey/walkthroughs/csv_score_report_generator/${id}`;

	// if sorting present, fix order naming and add to query params
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			fetchUtils
				.get(baseUrl + apiUrl, {})
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						reject(d.message);
						message.error(d.data);
					} else {
						resolve(d.data);
						const hide = message.loading(d.data, 0);
						setTimeout(hide, 7000);
					}
				})
				.catch((d) => {
					reject(d.message);
				});
		});
	};
};

// `params` becomes the query string: FetchUtils.getQueryString serialises every top-level key on a
// GET. Pass `{ export_mode: PDF_EXPORT_MODES.LARGE_PHOTOS }` for the larger-photo variant.
export const generateWalkthroughPDF =
	(roleType) =>
	(id, params = {}) => {
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${roleType}/site_survey/walkthroughs/${id}/generate_pdf`;
		return (dispatch) => {
			return new Promise((resolve, reject) => {
				fetchUtils
					.get(baseUrl + apiUrl, params)
					.then(parseJSON)
					.then(({ json, status }) => {
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (json.status === 'error') {
							reject(json.message);
						} else {
							resolve(json);
						}
					})
					.catch((err) => {
						reject(err.message || 'Failed to generate PDF.');
					});
			});
		};
	};

// The status endpoint is per-variant too — polling without the mode would read the standard export's
// progress into the larger-photo UI. `headers` is stripped from the query string by FetchUtils.
export const getWalkthroughPDFStatus =
	(roleType) =>
	(id, params = {}) => {
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${roleType}/site_survey/walkthroughs/${id}/pdf_status`;
		return (dispatch) => {
			return new Promise((resolve, reject) => {
				fetchUtils
					.get(
						baseUrl + apiUrl,
						Object.assign(
							{ headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
							params
						)
					)
					.then(parseJSON)
					.then(({ json, status }) => {
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						const d = json;
						if (d.status === 'error') {
							reject(d.message);
						} else {
							const data = d.data;
							const pdfLink = nullSafeGet('pdfLink', data);
							const pdfStatus = nullSafeGet('status', data);
							if (pdfLink) {
								resolve({ completed: true, pdfLink: pdfLink });
							} else if (pdfStatus === 'none') {
								resolve([]);
							} else {
								const total = nullSafeGetOrElse('pdfTotalImages', data, 0);
								const completed = nullSafeGetOrElse('pdfImagesDownloaded', data, 0);
								resolve([
									{
										completedRecords: completed,
										totalRecords: total || 1,
									},
								]);
							}
						}
					})
					.catch((err) => {
						reject(nullSafeGetOrElse('message', err, 'Failed to check PDF status.'));
					});
			});
		};
	};

const markWalkthroughComplete = (roleType) => (id) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/site_survey/walkthroughs/completed/${id}`;
	return (dispatch) => {
		dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updateStart({}));
		return new Promise((resolve, reject) => {
			fetchUtils
				.patch(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
					})
				)
				.then((response) => response.json())
				.then((d) => {
					if (d.status === 'error') {
						dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
						reject(d.message);
					} else {
						dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updateSuccess(d.data));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
					reject(d.message);
				});
		});
	};
};

export const markWalkthroughCompleteForBuyer = markWalkthroughComplete(ROLE_TYPES.BUYER);
export const markWalkthroughCompleteForSupplier = markWalkthroughComplete(ROLE_TYPES.SUPPLIER);

const refreshWalkthroughCounts = (userType) => (filters) => (dispatch) => {
	// Pipeline counts come from the OpenSearch `/search/count_by_status` endpoint (POST body).
	const apiUrl = `/api/v1/${userType}/site_survey/walkthroughs/search/count_by_status`;

	const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
	const data = flattenArrayValues({
		...(filters || {}),
		statuses: WalkthroughPipeLineFilters.map((pipelineFilter) => pipelineFilter.name).join(','),
		status: undefined,
	});

	const request = fetchUtils.post(getBackendUri() + apiUrl, { headers, data });

	request
		.then(parseJSON)
		.then(({ json }) => {
			const response = json;
			if (response.status === 'error') {
				message.error(response.message);
			} else {
				// The endpoint only returns rows for statuses that currently have walkthroughs (and an
				// empty array when none do). Dispatch a count for every pipeline status — defaulting to
				// 0 for any status missing from the response — so a status that drops to zero resets in
				// the UI instead of lingering at its previous (stale) count.
				const countsByStatus = nullSafeGetOrElse('data', response, []).reduce(
					(accumulator, category) => {
						accumulator[category.categoryName] = category.count;
						return accumulator;
					},
					{}
				);
				WalkthroughPipeLineFilters.forEach((pipelineFilter) => {
					dispatch(
						WALKTHROUGHS_CRUD_ACTION_CREATORS.countBySuccess(
							countsByStatus[pipelineFilter.name] || 0,
							pipelineFilter.name
						)
					);
				});
			}
		})
		.catch((error) => {
			message.error(error.message);
		});
};

export const refreshWalkthroughCountsForBuyer = refreshWalkthroughCounts(ROLE_TYPES.BUYER);
export const refreshWalkthroughCountsForSupplier = refreshWalkthroughCounts(ROLE_TYPES.SUPPLIER);

export type WalkthroughSyncScope = {
	walkthroughIds?: string;
	buyerCompanyIds?: string;
	syncAll?: boolean;
	updatedAtStartDate?: string;
	updatedAtEndDate?: string;
	parallelism?: number;
	batchSize?: number;
};

export const triggerWalkthroughOpenSearchSync = (scope: WalkthroughSyncScope) => () =>
	new Promise((resolve, reject) => {
		const apiUrl = `/api/v1/buyer/site_survey/walkthroughs/sync_to_opensearch`;
		fetchUtils
			.post(getBackendUri() + apiUrl, {
				headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				data: scope,
			})
			.then(parseJSON)
			.then(({ json }) => {
				if (json.status === 'error') {
					reject(json.message);
				} else {
					resolve(json.data);
				}
			})
			.catch((e) => reject(nullSafeGetOrElse('message', e, 'Failed to start sync.')));
	});

export const fetchWalkthroughOpenSearchSyncStatus = () => () =>
	new Promise((resolve, reject) => {
		const apiUrl = `/api/v1/buyer/akka_stream_audits/status?entityType=OpenSearchSync_Walkthrough&isRunning=true`;
		fetchUtils
			.get(getBackendUri() + apiUrl, {
				headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
			})
			.then(parseJSON)
			.then(({ json }) => {
				if (json.status === 'error') {
					reject(json.message);
				} else {
					resolve(json.data || []);
				}
			})
			.catch((e) => reject(nullSafeGetOrElse('message', e, 'Failed to fetch sync status.')));
	});

export const adjustWalkthroughSyncParallelism = (auditId: number, parallelism: number) => () =>
	new Promise((resolve, reject) => {
		const apiUrl =
			`/api/v1/buyer/akka_stream_audits/${auditId}/adjust_parallelism` +
			`?parallelism=${parallelism}`;
		fetchUtils
			.post(getBackendUri() + apiUrl, {
				headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
			})
			.then(parseJSON)
			.then(({ json }) => {
				if (json.status === 'error') {
					reject(json.message);
				} else {
					resolve(json.data);
				}
			})
			.catch((e) => reject(nullSafeGetOrElse('message', e, 'Failed to adjust parallelism.')));
	});

export const cancelWalkthroughOpenSearchSync = (auditId: number) => () =>
	new Promise((resolve, reject) => {
		const apiUrl = `/api/v1/buyer/akka_stream_audits/${auditId}/cancel`;
		fetchUtils
			.post(getBackendUri() + apiUrl, {
				headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
			})
			.then(parseJSON)
			.then(({ json }) => {
				if (json.status === 'error') {
					reject(json.message);
				} else {
					resolve(json.data);
				}
			})
			.catch((e) => reject(nullSafeGetOrElse('message', e, 'Failed to cancel sync.')));
	});

const fetchWalkthroughNotes = (roleType) => (walkthroughId) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/site_survey/walkthroughs/${walkthroughId}/notes`;
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(fetchAllMessagesStart(walkthroughId));
			fetchUtils
				.get(baseUrl + apiUrl, {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				})
				.then(parseJSON)
				.then(({ json, status }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(fetchAllMessagesError(d.message, walkthroughId));
						reject(d.message);
					} else {
						dispatch(fetchAllMessagesSuccess(walkthroughId, normalizeNotesResponse(d.data)));
						resolve(normalizeNotesResponse(d.data));
					}
				})
				.catch((d) => {
					dispatch(fetchAllMessagesError(d.message, walkthroughId));
					reject(d.message);
				});
		});
	};
};

export const fetchWalkthroughNotesForBuyer = fetchWalkthroughNotes(ROLE_TYPES.BUYER);
export const fetchWalkthroughNotesForSupplier = fetchWalkthroughNotes(ROLE_TYPES.SUPPLIER);

// Shared by the singular and bulk append endpoints — both return the same flat note
// array and refresh the same message channel.
const appendWalkthroughNotesRequest = (apiUrl, entity) => {
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updateStart(entity));
			fetchUtils
				.patch(apiUrl, {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
					data: entity,
				})
				.then(parseJSON)
				.then(({ json, status }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updateError(d.message, entity));
						reject(d.message);
					} else {
						dispatch(fetchAllMessagesSuccess(entity.id, normalizeNotesResponse(d.data)));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updateError(d.message, entity));
					reject(d.message);
				});
		});
	};
};

// entity must include id and note fields
const appendWalkthroughNotes = (roleType) => (entity) =>
	appendWalkthroughNotesRequest(
		`${getBackendUri()}/api/v1/${roleType}/site_survey/walkthroughs/${entity.id}/append_notes`,
		entity
	);

export const appendWalkthroughNotesForBuyer = appendWalkthroughNotes(ROLE_TYPES.BUYER);
export const appendWalkthroughNotesForSupplier = appendWalkthroughNotes(ROLE_TYPES.SUPPLIER);

// entity must include id and a notes array. Photo-only server-side, and it publishes a
// single grouped note notification for the whole batch instead of one per note.
const appendWalkthroughNotesBulk = (roleType) => (entity) =>
	appendWalkthroughNotesRequest(
		`${getBackendUri()}/api/v1/${roleType}/site_survey/walkthroughs/${entity.id}/append_notes/bulk`,
		entity
	);

export const appendWalkthroughNotesBulkForBuyer = appendWalkthroughNotesBulk(ROLE_TYPES.BUYER);
export const appendWalkthroughNotesBulkForSupplier = appendWalkthroughNotesBulk(
	ROLE_TYPES.SUPPLIER
);
